import React, { useState, useEffect } from 'react'
import { FaArrowLeft, FaArrowRight, FaCheck } from 'react-icons/fa'
import { Link, useParams } from 'react-router'
import { Heading, Box, Stack, ResponsiveStack, Text, Button, Icon, Input, Loading, Badge, Link as ChakraLink } from '@/components/lib'
import api from '../../../api'
import utils from '@/utils/utils'
import useStore from '../../../store'

import EmptyBox from '../../includes/EmptyBox'
import ParticipantMessenger from '../../includes/ParticipantMessenger'

function Messages () {
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [participants, setParticipants] = useState([])
  const [nextPageAvailable, setNextPageAvailable] = useState(false)
  const [prevPageAvailable, setPrevPageAvailable] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [onlyUnreads, setOnlyUnreads] = useState(false)
  const [participant, setParticipant] = useState()

  const { participantId } = useParams()
  const { user, trial } = useStore()
  const subscriptionTopic = `trial.${trial._id}.participantMessages`
  const [hasSubscription, setHasSubscription] = useState(user?.subscriptions?.email?.includes(subscriptionTopic))

  const trialId = trial?._id

  useEffect(() => {
    getParticipants()
  }, [trialId, page, searchTerm, onlyUnreads])

  useEffect(() => {
    const participant = participants?.find(p => p._id === participantId)
    if (participant) {
      setParticipant(participant)
    }
  }, [participantId, participants])

  function getParticipants () {
    setLoading(true)
    api.trials.getParticipants(trialId, page, searchTerm, null, onlyUnreads, null, null, ({ participants, nextPageAvailable, prevPageAvailable, total }) => {
      setLoading(false)
      setParticipants(participants)
      setNextPageAvailable(nextPageAvailable)
      setPrevPageAvailable(prevPageAvailable)
    }, err => {
      setLoading(false)
      utils.toast('error', err.message)
    })
  }

  function subscribe (enabled) {
    const f = enabled ? api.users.createEmailSubscription : api.users.deleteEmailSubscription
    f(user._id, subscriptionTopic, subData => {
      utils.toast('success', 'Successfully updated subscription')
      setHasSubscription(subData?.subscriptions?.email?.includes(subscriptionTopic))
    }, err => {
      utils.toast('error', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <Box display={{ md: 'flex' }} justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Participant messaging</Heading>
          <Text>Provide support to your participants using the messenger.</Text>
        </Box>
        <Box>
          {hasSubscription
            ? <Button onClick={() => subscribe(false)} size='sm' colorPalette='primary' variant='outline'>Unsubscribe from email notifications</Button>
            : <Button onClick={() => subscribe(true)} size='sm' colorPalette='primary'>Subscribe to email notifications</Button>}
        </Box>
      </Box>

      <Stack direction='row' gap={5}>
        <Stack w='400px' bg='bg.surface' borderWidth='1px' borderColor='border.default' p={5} borderRadius={3}>
          <ResponsiveStack alignItems='baseline' justifyContent='space-between'>
            <Heading as='h3' size='sm'>Participant list</Heading>
            <Button size='xs' variant={onlyUnreads ? 'solid' : 'outline'} onClick={e => setOnlyUnreads(!onlyUnreads)}>{onlyUnreads && <Icon as={FaCheck} />} Only show unreads</Button>
          </ResponsiveStack>

          <Input placeholder='Search for a participant...' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />

          {!loading && !participants?.length &&
            <EmptyBox icon='people' title='Participants will appear here once they join the trial' />}

          {loading && <Loading />}
          {participants?.map(p =>
            <Box key={p._id} as={Link} to={`/trials/${trial._id}/messages/${p._id}`} display='flex' justifyContent='space-between' bg={participantId === p._id ? 'bg.muted' : 'bg.subtle'} alignItems='baseline' p={2} rounded='md'>
              <Text>{p.id}</Text>
              {p.unreadMessageCount > 0 && <Badge colorPalette='yellow' size='sm'>{p.unreadMessageCount} unread</Badge>}
              {p.lastSeenAt && <Text fontSize='xs'>Last seen {utils.formatDateTime(p.lastSeenAt)}</Text>}
            </Box>
          )}
          {prevPageAvailable && <Button onClick={e => setPage(page - 1)}><Icon as={FaArrowLeft} /></Button>}
          {nextPageAvailable && <Button onClick={e => setPage(page + 1)}><Icon as={FaArrowRight} /></Button>}
        </Stack>
        <Stack flex={1}>
          {participant && <Heading as='h3' size='md'>Messages with <ChakraLink as={Link} to={`/trials/${trial._id}/participants/${participant._id}`} color='primary.500' variant='underline'>{participant.id}</ChakraLink></Heading>}
          <ParticipantMessenger trial={trial} participant={participant} />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Messages
